/* eslint-disable import/order */
/* eslint-disable max-len */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useQueryClient } from "react-query";
import {
  selectReadOnlyMode,
  selectConfigAxes,
  setConfiguration,
  setCurrentStep,
  selectApplicationSettings,
  addNewAxis,
  showSnackbar,
  setHighlightApplicationSettingsErrors,
  selectApplicationSettingsChanged,
  setApplicationSettingsChanged,
  selectRetrofitSteps,
  selectRetrofitCurrentStep,
  selectRetrofitConfiguration,
  selectSizingSelected
} from "../../../Retrofit/store/slices/retrofit";

import { logEvent, updateConfigurationApi } from "../../../Common/utils/api/api";
import t from "../../../Common/utils/translation/translator";
import useCheckMobileScreen from "../../../Common/utils/useCheckMobileScreen";
import {
  selectEventsData,
  selectUpdateConfiguration,
  setUpdatingConfiguration,
  updateConfiguration
} from "../../../Common/store/slices/common";

import Stepper from "shared-components/lib/iac-stepper";
import {
  buildVirtualPageViewDataLayerEvent,
  logDataLayerEvent
} from "../../../Common/utils/dataLayerAnalytics/dataLayerAnalytics";

/**
 * Sub header of the app.
 */
const RetrofitStepper = () => {
  const steps = useSelector(selectRetrofitSteps);
  const currentStep = useSelector(selectRetrofitCurrentStep);
  const configuration = useSelector(selectRetrofitConfiguration);
  const configAxes = useSelector(selectConfigAxes);
  const dispatch = useDispatch();

  useEffect(() => {
    logDataLayerEvent(buildVirtualPageViewDataLayerEvent(configuration?.configId, currentStep?.label));
  }, [currentStep]);

  const [savedConfigurationPayload, setSavedConfigurationPayload] = useState(null);

  const {
    refetch: execUpdateConfiguration,
    isLoading,
    isFetching
  } = useQuery(
    "UPDATE_CONFIGURATION",
    async () => {
      try {
        // console.log("updating configuration....");
        // check if the savedConfigurationPayload is not the same than the current configuration
        if (JSON.stringify(savedConfigurationPayload) !== JSON.stringify(configuration.configData)) {
          setSavedConfigurationPayload(configuration.configData);
          // console.log(configuration);
          // remove bundle.bundleData.[motor, drive, gearbox and adaptationKit].translationData from the payload
          // from each axis (each motor, drive, gearbox and adaptationKit
          const payloadWithoutUnnecessaryData = {
            ...configuration.configData,
            axes: configuration.configData.axes.map((axis) => {
              // each axis had a bundle.bundleData.[motor, drive, gearbox and adaptationKit].translationData
              // keep only the translationData.[0].catalogue_product.long_desc
              const axisUnnecessaryData = {
                ...axis,
                bundle: {
                  ...axis.bundle,
                  bundleData: {
                    ...axis.bundle.bundleData,
                    motor: {
                      ...axis.bundle?.bundleData?.motor,
                      translationData: axis.bundle?.bundleData?.motor?.translationData?.map((translation) => ({
                        catalogue_product: {
                          long_desc: translation?.catalogue_product?.long_desc,
                          ean: translation?.catalogue_product?.ean,
                          e_num: translation?.catalogue_product?.e_num
                        }
                      })),
                      description: axis.bundle?.bundleData?.motor?.description,
                      motorSpecificAttributesList: null,
                      ipProtectionAssociations: null
                    },
                    drive: {
                      ...axis.bundle?.bundleData?.drive,
                      translationData: axis.bundle?.bundleData?.drive?.translationData?.map((translation) => ({
                        catalogue_product: {
                          long_desc: translation?.catalogue_product?.long_desc,
                          ean: translation?.catalogue_product?.ean,
                          e_num: translation?.catalogue_product?.e_num
                        }
                      })),
                      description: axis.bundle?.bundleData?.drive?.description,
                      driveVoltages: null,
                      driveCommunicationInterfaces: null,
                      driveOutputCurrents: null
                    },
                    gearbox: {
                      ...axis.bundle?.bundleData?.gearbox,
                      translationData: axis.bundle?.bundleData?.gearbox?.translationData?.map((translation) => ({
                        catalogue_product: {
                          long_desc: translation?.catalogue_product?.long_desc,
                          ean: translation?.catalogue_product?.ean,
                          e_num: translation?.catalogue_product?.e_num
                        }
                      })),
                      description: axis.bundle?.bundleData?.gearbox?.description
                    },
                    adaptationKit: {
                      ...axis.bundle?.bundleData?.adaptationKit,
                      translationData: axis.bundle?.bundleData?.adaptationKit?.translationData?.map((translation) => ({
                        catalogue_product: {
                          long_desc: translation?.catalogue_product?.long_desc,
                          ean: translation?.catalogue_product?.ean,
                          e_num: translation?.catalogue_product?.e_num
                        }
                      })),
                      description: axis.bundle?.bundleData?.adaptationKit?.description
                    }
                  },
                  axisState: {
                    ...axis.axisState,
                    bundlesToCompare: null
                  }
                },
                axisState: {
                  ...axis.axisState,
                  savedApplicationDataPayload: null
                }
              };
              return axisUnnecessaryData;
            })
          };

          // console.log("payloadWithoutUnnecessaryData", payloadWithoutUnnecessaryData);

          // console.log("updating configuration....");
          // update configuration with a new status
          // console.log("updating configuration....", configuration.configId);
          const updatedConfig = await updateConfigurationApi(configuration.configId, payloadWithoutUnnecessaryData);
          // dispatch(setConfiguration(updatedConfig.updatedConfigurationData));
          // console.log("updated !", updatedConfig);
          // if we're in the finalization step, we need to display a snackbar
          if (currentStep === steps.SAVEANDSHARE && !isLoading) {
            dispatch(showSnackbar({ id: "CONFIGURATION_SAVE" }));
          }
          return updatedConfig;
        }
        // console.log("no need to update configuration");
        return null;
      } catch (error) {
        console.error("ERR", error.response?.status);
      }
      return undefined;
    },
    {
      enabled: false
    }
  );

  useEffect(() => {
    dispatch(setUpdatingConfiguration(isLoading || isFetching));
  }, [isLoading, isFetching]);

  const modeReadOnly = useSelector(selectReadOnlyMode);
  const queryClient = useQueryClient();

  const runUpdateConfiguration = useSelector(selectUpdateConfiguration);

  useEffect(() => {
    if (configuration?.configId && currentStep && !modeReadOnly) {
      // console.log("Configuration changed => update CONF");
      queryClient.cancelQueries("UPDATE_CONFIGURATION");
      execUpdateConfiguration();
    } else {
      // console.log(
      //   "no configId or currentStep = null or READ ONLY => no update"
      // );
    }
  }, [runUpdateConfiguration]);

  useEffect(() => {
    if (configuration?.configId && currentStep && !modeReadOnly) {
      // config changed => update status and currentStepId
      dispatch(
        setConfiguration({
          ...configuration,
          configData: {
            ...configuration.configData,
            currentStepId: currentStep.id,
            configStatus: currentStep.status
          }
        })
      );
    } else {
      // console.log(
      //   "no configId or currentStep = null or READ ONLY => no update"
      // );
    }
  }, [configAxes]);

  const isMobile = useCheckMobileScreen();

  const applicationSettings = useSelector(selectApplicationSettings);

  const [applicationSettingsFilled, setApplicationSettingsFilled] = useState(false);

  const applicationSettingsChanged = useSelector(selectApplicationSettingsChanged);

  const handleGoToApplicationSettings = () => {
    dispatch(setCurrentStep(steps.APPLICATION_SETTINGS));
    dispatch(updateConfiguration());
  };

  const handleGoToSpecifySelect = () => {
    if (!applicationSettingsFilled) {
      dispatch(setHighlightApplicationSettingsErrors(true));
      return;
    }
    if (configAxes.length === 0) {
      dispatch(addNewAxis());
    }

    // applicationSettingsChanged is !firstVisit && !isEqual(initialFilters, data)
    if (applicationSettingsChanged) {
      dispatch(showSnackbar({ id: "APPLICATION_SETTINGS_CHANGED" }));
      dispatch(setApplicationSettingsChanged(false));
    }

    dispatch(setCurrentStep(steps.SPECIFY_SELECT));
    dispatch(updateConfiguration());
  };

  // const handleGoToSELECT = () => {
  //   if (isMobile) {
  //     dispatch(setShowContinueOnDesktopPopup(true));
  //     return;
  //   }
  //   dispatch(setCurrentStep(steps.SELECT));
  //   dispatch(updateConfiguration());
  // };

  const eventsData = useSelector(selectEventsData);

  const applicationSettingStepLabel = () => {
    if (isMobile) {
      return applicationSettingsFilled ? t("msizer.ui.stepper.done") : t("msizer.ui.stepper.not_done");
    }
    return applicationSettingsFilled
      ? `${applicationSettings?.driveVoltages?.[0]} - ${applicationSettings?.driveInterface?.[0]}`
      : t("msizer.ui.stepper.not_done");
  };

  const handleGoToSAVEANDSHARE = () => {
    if (configAxes.length > 0 && configAxes.every((axis) => axis.bundle.motorReference)) {
      dispatch(setCurrentStep(steps.SAVEANDSHARE));
      dispatch(updateConfiguration());
      if (currentStep !== steps.SAVEANDSHARE) {
        logEvent(eventsData.locale, eventsData.types.save, eventsData.tags.none, configuration?.configId);
      }
    } else {
      dispatch(showSnackbar({ id: "MISSING_BUNDLE_FINALIZE" }));
      logEvent(eventsData.locale, eventsData.types.unfinishedConf, eventsData.tags.none, configuration?.configId);
    }
  };

  useEffect(() => {
    setApplicationSettingsFilled(
      applicationSettings?.driveInterface?.length > 0 &&
        applicationSettings?.nbPhases?.length > 0 &&
        applicationSettings?.driveVoltages?.length > 0
    );
  }, [applicationSettings]);

  const isSizingSelected = useSelector(selectSizingSelected);
  const servoStepper = currentStep !== steps.START && (
    <>
      <Stepper
        steps={[
          {
            action: () => handleGoToApplicationSettings(),
            active: true,
            done: currentStep > steps.APPLICATION_SETTINGS,
            hidden: false,
            id: "APPLICATION_SETTINGS",
            isActiveStep: currentStep === steps.APPLICATION_SETTINGS,
            isActive: currentStep === steps.APPLICATION_SETTINGS,
            name: steps.APPLICATION_SETTINGS.label,
            order: 1,
            skipped: false,
            subLabel:
              currentStep === steps.APPLICATION_SETTINGS
                ? t("msizer.ui.stepper.in_progress")
                : applicationSettingStepLabel()
          },
          {
            action: () => handleGoToSpecifySelect(),
            active: applicationSettingsFilled,
            done: currentStep > steps.SPECIFY_SELECT,
            hidden: false,
            id: "SPECIFY_SELECT",
            isActiveStep:
              currentStep === steps.SPECIFY_SELECT || currentStep === steps.SPECIFY || currentStep === steps.SELECT,
            isActive:
              currentStep === steps.SPECIFY_SELECT || currentStep === steps.SPECIFY || currentStep === steps.SELECT,
            name: steps.SPECIFY_SELECT.label,
            order: 2,
            skipped: false,
            subLabel:
              currentStep === steps.SPECIFY_SELECT || currentStep === steps.SPECIFY || currentStep === steps.SELECT
                ? t("msizer.ui.stepper.in_progress")
                : currentStep === steps.SAVEANDSHARE
                  ? t("msizer.ui.stepper.done")
                  : t("msizer.ui.stepper.not_done")
          },
          {
            action: () => handleGoToSAVEANDSHARE(),
            active: configAxes.length > 0 && configAxes.every((axis) => axis.bundle.motorReference),
            done: currentStep === steps.SAVEANDSHARE,
            hidden: false,
            id: "SAVEANDSHARE",
            isActiveStep: currentStep === steps.SAVEANDSHARE,
            isActive: currentStep === steps.SAVEANDSHARE,
            name: steps.SAVEANDSHARE.label,
            order: 3,
            skipped: false,
            subLabel: currentStep === steps.SAVEANDSHARE ? t("msizer.ui.stepper.done") : t("msizer.ui.stepper.not_done")
          }
        ]}
        mobileBreakpoint={800}
        disabled={false}
      ></Stepper>
    </>
  );

  return !isSizingSelected && servoStepper;
};

RetrofitStepper.propTypes = {};

RetrofitStepper.defaultProps = {};

export default RetrofitStepper;
